/** Cell-buffer pixel effects for beams, fire, and tracers. */
import { bayer, hex } from "../shared/color";
import type { PixBuf } from "./rig";

export const WHITE = hex("#ffffff");
export const CYAN = hex("#7ff3ff");
export const CYAN_DEEP = hex("#1ab8e6");
export const CYAN_HOT = hex("#d6ffff");
export const YELLOW = hex("#ffe26b");
export const ORANGE = hex("#ff8a2a");
export const HOT = hex("#fff3a6");
export const DEEP = hex("#f2561d");
export const GOLD = hex("#ffc23d");
export const RED = hex("#c9281f");
const ASH = hex("#8a1f17");
const SOOT = hex("#4a1712");
/** Fire colors from white through gold into soot. */
export const FIRE = [WHITE, HOT, YELLOW, GOLD, ORANGE, DEEP, RED, ASH, SOOT];
export interface BeamFx {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  life: number;
  max: number;
}

export interface CellPart {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  seed: number;
  kind: string;
  tint?: number;
}

/** Fireball, chunk, flame, and spark streaks. Sizes are world pixels. */
export function paintCellPart(buf: PixBuf, light: PixBuf, p: CellPart, time: number) {
  const j = p.max > 0 ? p.life / p.max : 0;
  const gx = p.x / 2;
  const gy = p.y / 2;
  if (p.kind === "fireball" || p.kind === "flame") {
    const age = 1 - j;
    const cells = p.size / 2;
    if (p.kind === "flame" && age < 0.03) {
      buf.disc(gx, gy, cells * 0.95, hex("#96e6ff"));
      buf.set(Math.floor(gx), Math.floor(gy), WHITE);
      return;
    }
    const along = p.kind === "flame"
      ? Math.min(0.999, age * 1.12 + (p.seed % 1) * 0.08)
      : Math.min(0.999, Math.pow(age, 0.62) + (p.seed % 1) * 0.1);
    const idx = p.kind === "flame"
      ? Math.min(FIRE.length - 1, Math.floor(along * FIRE.length))
      : Math.min(FIRE.length - 2, Math.floor(along * (FIRE.length - 1)));
    const rise = p.kind === "flame" ? 1 : Math.min(1, age * 7);
    const radius = p.kind === "flame"
      ? cells * (0.55 + Math.pow(age, 0.6) * 1.9)
      : cells * (0.55 + 0.45 * rise) * (1 - age * 0.55);
    const color = FIRE[idx];
    if (idx >= FIRE.length - 2) {
      buf.alphaDisc(gx, gy, radius, color, Math.min(1, j * 3) * (p.kind === "flame" ? 0.8 : 0.85));
      return;
    }
    buf.disc(gx, gy, radius, FIRE[Math.min(FIRE.length - 1, idx + 2)]);
    buf.disc(gx - radius * (p.kind === "flame" ? 0.14 : 0.16), gy - radius * (p.kind === "flame" ? 0.2 : 0.22), radius * (p.kind === "flame" ? 0.76 : 0.78), color);
    if (p.kind === "fireball" && radius > 1.6) buf.disc(gx, gy - radius * 0.9, radius * 0.42, color);
    if (idx <= 3 && radius > (p.kind === "flame" ? 1.6 : 1.8)) {
      buf.disc(gx - radius * (p.kind === "flame" ? 0.25 : 0.28), gy - radius * (p.kind === "flame" ? 0.3 : 0.34), radius * (p.kind === "flame" ? 0.34 : 0.36), FIRE[Math.max(0, idx - 1)]);
    }
    if (idx < 6) light.glow(gx, gy, radius * (p.kind === "flame" ? 2.6 : 2.4) + (p.kind === "flame" ? 3 : 2), color, (p.kind === "flame" ? 0.14 : 0.18) * j);
    return;
  }
  if (p.kind === "chunk") {
    const flick = Math.sin(time * 40 + p.seed) > 0;
    buf.disc(gx, gy, 1.6, DEEP);
    buf.set(Math.floor(gx), Math.floor(gy), flick ? HOT : YELLOW);
    buf.set(Math.floor(gx) + 1, Math.floor(gy), YELLOW);
    light.glow(gx, gy, 7, ORANGE, 0.45);
    return;
  }
  if (p.kind === "ember") {
    const flick = Math.sin(time * 22 + p.seed * 3) > -0.2;
    const color = j > 0.5 ? (flick ? YELLOW : ORANGE) : j > 0.2 ? ORANGE : RED;
    buf.set(Math.floor(gx), Math.floor(gy), color);
    light.glow(gx, gy, 3, color, 0.3 * j);
    return;
  }
  const ion = p.kind === "ion";
  const color = ion ? (j > 0.5 ? WHITE : j > 0.25 ? CYAN : CYAN_DEEP) : (j > 0.6 ? HOT : j > 0.3 ? YELLOW : ORANGE);
  const speed = Math.hypot(p.vx, p.vy) || 1;
  const len = Math.min(9, 1 + speed * 0.011);
  const ux = p.vx / speed;
  const uy = p.vy / speed;
  const tail = ion ? CYAN_DEEP : DEEP;
  buf.line(gx - ux * len, gy - uy * len, gx - ux * len * 0.4, gy - uy * len * 0.4, j > 0.5 ? color : tail);
  buf.line(gx - ux * len * 0.45, gy - uy * len * 0.45, gx, gy, color);
  light.glow(gx, gy, 3, color, 0.45 * j);
}

export function drawBeam(buf: PixBuf, light: PixBuf, beam: BeamFx) {
  const o = beam.life / beam.max;
  const x0 = beam.x0 / 2;
  const y0 = beam.y0 / 2;
  const x1 = beam.x1 / 2;
  const y1 = beam.y1 / 2;
  const len = Math.hypot(x1 - x0, y1 - y0) || 1;
  const ux = (x1 - x0) / len;
  const uy = (y1 - y0) / len;
  const fade = Math.pow(o, 0.6);
  const wide = 6.5 * fade + 0.6;
  const mid = wide * 0.62;
  const core = o > 0.4 ? 1.8 * fade + 0.4 : o > 0.2 ? 0.6 : 0;
  const px = -uy;
  const py = ux;
  for (let d = 0; d <= len; d += 0.5) {
    const x = x0 + ux * d;
    const y = y0 + uy * d;
    for (let w = -wide; w <= wide; w += 0.5) {
      const ix = Math.floor(x + px * w);
      const iy = Math.floor(y + py * w);
      const m = Math.abs(w);
      if (m <= core) buf.set(ix, iy, WHITE);
      else if (m <= mid) buf.set(ix, iy, m < core + 1 ? CYAN_HOT : CYAN);
      else if (bayer(ix, iy) < o * (1 - (m - mid) / (wide - mid + 0.01)) + 0.15) buf.set(ix, iy, CYAN_DEEP);
    }
    if (d % 5 === 0) light.glow(x, y, 12 * fade + 3, CYAN_DEEP, 0.4 * o);
  }
  light.glow(x0, y0, 22 * fade, CYAN, 0.9 * o);
}

